import { useState } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function NewClientDialog({ onCreated }) {
  const { clientOpen, setClientOpen, refresh } = useAdmin();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ name: "", email: "", phone: "", company: "" });

  const save = async (addService) => {
    if (!form.name || !form.email) {
      toast.error("Client name and email are required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/clients", form);
      toast.success("Client saved");
      setClientOpen(false);
      reset();
      refresh();
      if (onCreated) onCreated(res.data, addService);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save client");
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
    className: "bg-white border-[#E2EBF0] focus:border-[#10B981] focus:ring-2 focus:ring-[#67F2B3]/40",
  });

  return (
    <Dialog open={clientOpen} onOpenChange={setClientOpen}>
      <DialogContent className="sm:max-w-md" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0D3B2D]">New Client</DialogTitle>
          <DialogDescription className="text-[#4A635A]">Add a customer to your CRM. Only name and email are required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-[#0D3B2D]">Client Name *</Label>
            <Input {...field("name")} data-testid="client-name-input" placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-[#0D3B2D]">Email *</Label>
            <Input {...field("email")} type="email" data-testid="client-email-input" placeholder="jane@company.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#0D3B2D]">Phone</Label>
              <Input {...field("phone")} data-testid="client-phone-input" placeholder="Optional" />
            </div>
            <div>
              <Label className="text-[#0D3B2D]">Company</Label>
              <Input {...field("company")} data-testid="client-company-input" placeholder="Optional" />
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-4">
          <Button onClick={() => save(false)} disabled={saving} data-testid="save-client-button"
            className="flex-1 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">Save Client</Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="save-client-add-service-button"
            className="flex-1 bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
            Save &amp; Add Service
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
